/**
 * Annaprashan decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const annaprashanDecorationsPack: ToilePack = {
    slug: 'annaprashan-decorations',
    category: 'occasions',
    name: 'Annaprashan decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'backdrop', label: 'Backdrop', subject: 'a flower wall with the baby\'s name "Aarav" in gold cut-out letters for the rice ceremony'},
        {key: 'seat', label: 'Floor seat', subject: 'a low seat with a banana leaf and brass bowls'},
        {key: 'thali', label: 'Thali', subject: 'a decorated brass thali with rice kheer'},
    ],
    styles: [
        {key: 'bengali', label: 'Bengali', style: 'a traditional Bengali setting with white alpana floor art, red-bordered cloth and shola pith ornaments, an eye-level view', palette: 'red and white with brass gold', framing: 'the setup centered with the floor art in front'},
        {key: 'southern', label: 'South Indian', style: 'a South Indian setting with a mango leaf toran, a rice-flour kolam and banana stems at the sides, a slightly high view', palette: 'leaf green, marigold and brass', framing: 'the setup in the lower two thirds'},
        {key: 'pastel', label: 'Pastel', style: 'a modern pastel setting with paper flowers, a round arch and soft cushions, a three-quarter view', palette: 'blush pink, mint and cream', framing: 'the setup filling the frame'},
    ],
    directions: ['baby girl', 'peacock theme', 'simple at home'],
    directionHint: 'try: baby girl',
    seo: {
        title: 'Annaprashan decoration: backdrop, floor seat, thali',
        metaDescription: 'Nine annaprashan decoration pictures for a rice ceremony at home: a flower backdrop, a floor seat and a brass thali in three styles.',
        h1: 'Annaprashan decoration: backdrop, floor seat, thali',
        tagline: 'Nine pictures, three pieces of the ceremony, each set in a Bengali, a South Indian and a pastel style.',
        intro: "Annaprashan decoration for a baby's rice ceremony: a flower backdrop with a name in gold letters, a low floor seat with a banana leaf and brass bowls, and a brass thali with rice kheer. Each piece appears in a Bengali style with white alpana floor art, a South Indian style with a mango leaf toran, or a pastel style with paper flowers. Type a twist, such as baby girl or peacock theme, and the same setups carry it.",
        useCases: [
            'a rice ceremony at home',
            'a flower backdrop for family photos',
            'a floor seat setting for relatives',
            'a reference for a regional style',
        ],
        faq: [
            {q: 'What are the three styles?', a: 'Bengali, with white alpana floor art and red-bordered cloth; South Indian, with a mango leaf toran, a rice-flour kolam and banana stems; and pastel, with paper flowers, a round arch and soft cushions.'},
            {q: 'Does the backdrop show a name?', a: 'Yes. The backdrop shows a sample name in gold cut-out letters. The other pictures have no lettering.'},
            {q: 'Can I ask for a baby girl or a peacock theme?', a: 'Yes. Type the twist, and the same three pieces are shown in it.'},
            {q: 'Are these real photographs?', a: 'No. They are generated pictures made to look like photographs. There are no people in them.'},
        ],
    },
}
